import { useState, useCallback, useEffect } from "react";
import { Asset } from "@switcherstudio/switcher-api-client";
import { client } from "api/client";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "store/reducers";
import { addNotification } from "store/notification/slice";
import { NotificationType } from "store/notification/types";
import { setLoading } from "store/loading/slice";
import { useTranslation } from "react-i18next";
import rollbar from "helpers/rollbar";
import { AppDispatch } from "store/store";
import { closeCurrentModal, setActiveModal } from "store/modal/slice";
import { Modals } from "store/modal/types";
import { AttentionModal } from "components/modal/AttentionModal";

export function useRawFileDetails(assetId: string) {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();

    const { userInfo } = useSelector((s: RootState) => s.user);
    const [rawFile, setRawFile] = useState<Asset>();
    const [assetsUsingRawFile, setAssetsUsingRawFile] = useState<Asset[]>([]);
    const [disable, setDisable] = useState<boolean>(false);

    useEffect(() => {
        async function run() {
            try {
                dispatch(setLoading(1));
                const asset = await client.userAssets_GetUserAsset(
                    userInfo?.User?.Details?.Id || "",
                    assetId
                );

                if (asset.RequiredByAssetIds) {
                    let requiredByAssets = [];
                    for (const ra of asset?.RequiredByAssetIds) {
                        const requiredAsset =
                            await client.userAssets_GetUserAsset(
                                userInfo?.User?.Details?.Id || "",
                                ra
                            );

                        requiredByAssets.push(requiredAsset);
                    }

                    const assetsUseOtherFiles = requiredByAssets
                        .map((ra) => ra.RequiresAssetIds)
                        .some((ra) => ra?.length && ra.length > 1);

                    if (assetsUseOtherFiles) {
                        setDisable(assetsUseOtherFiles);
                    }

                    setAssetsUsingRawFile(requiredByAssets);
                }
                setRawFile(asset);
                dispatch(setLoading(-1));
            } catch (e) {
                rollbar.error("Error retrieving asset", e);
                dispatch(
                    addNotification({
                        type: NotificationType.Danger,
                        message: "errors:asset-retrieval-error"
                    })
                );
                dispatch(setLoading(-1));
            }
        }

        run();
    }, [dispatch, assetId, userInfo, t]);

    const closeAttentionModal = useCallback(() => {
        dispatch(closeCurrentModal());
    }, [dispatch]);

    const remove = useCallback(async () => {
        if (rawFile) {
            dispatch(
                setActiveModal({
                    id: Modals.AttentionModal,
                    type: Modals.AttentionModal,
                    component: (
                        <AttentionModal
                            isOpen={true}
                            setIsOpen={closeAttentionModal}
                            handleCancel={closeAttentionModal}
                            hasContinueButton={true}
                            handleContinue={async () => {
                                try {
                                    dispatch(setLoading(1));
                                    await client.userAssets_DeleteUserAssetsAll(
                                        userInfo?.User?.Details?.Id || "",
                                        [rawFile, ...assetsUsingRawFile]
                                    );
                                    dispatch(setLoading(-1));
                                    navigate("/switcher-cloud");
                                } catch (e) {
                                    dispatch(
                                        addNotification({
                                            type: NotificationType.Danger,
                                            message:
                                                "errors:asset-removal-error"
                                        })
                                    );
                                    dispatch(setLoading(-1));
                                }
                            }}
                            continueText={t("buttons:confirm")}
                        >
                            <div>
                                {!!assetsUsingRawFile.length ? (
                                    <div>
                                        <p>{t("asset-hook:confirm-4")}</p>
                                        <ul>
                                            {assetsUsingRawFile.map((a) => (
                                                <li key={a.Id}>{a.Name}</li>
                                            ))}
                                        </ul>
                                    </div>
                                ) : (
                                    t("messages:delete-raw-confirmation")
                                )}
                            </div>
                        </AttentionModal>
                    )
                })
            );
        }
    }, [
        dispatch,
        navigate,
        rawFile,
        assetsUsingRawFile,
        userInfo,
        t,
        closeAttentionModal
    ]);

    return {
        rawFile,
        assetsUsingRawFile,
        disable,
        remove
    };
}
